import { Card, Panel, ConfirmModal, Button } from '@/components/Bicester';
import Image from '@/components/Bicester/Image';
import { Form } from '@/components/formik-rsuite';
import { LEVEL_TYPE } from '@/constants';
import { fetchServiceMemberInfo } from '@/services/site';
import { fetchAddEditLose } from '@/services/site/lAndF';
import { getPathname, push } from '@/utils/history';
import { ACCOUNT_TABS } from '@/views/CustomerAccount';
import { useRequest } from 'ahooks';
import { FormikProvider, useFormik } from 'formik';
import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import { Alert, IconButton } from 'rsuite';
import { Member } from '..';
import { useModal } from '@ebay/nice-modal-react';
import { LostAndFoundContext } from '.';
import { BICESTER_FULL_DATE_HOURS_MINUTES } from '@/constants/dateFormat';
import { defaultLoseInfo, loseSchema } from './constants';
import { isBefore, subDays } from 'date-fns';
import PageLoader from '@/components/shared/PageLoader';
import useLoading from '@/hooks/useLoading';
import { ReadyContext } from '@/providers/ReadyProvider';
import useGePcsList from '@/hooks/preload/useGePcsList';
import { SERVICE_STATUS } from '@/services/constants';

const CreateLose = () => {
  const { profile } = React.useContext(ReadyContext);
  const { data: gePcsList, loading: gePcsListLoading } = useGePcsList();
  const { loseBasic, loading: loseBasicLoading } = React.useContext(LostAndFoundContext);
  const modal = useModal(ConfirmModal);

  const [content, setContent] = useState('');
  const [currentMember, setCurrentMember] = useState<Member>(null);
  const [defaultInfo, setDefaultInfo] = useState({ ...defaultLoseInfo, content: '' });

  const { run: addEditLose, loading: addEditLoseLoading } = useRequest(fetchAddEditLose, {
    manual: true,
  });

  useEffect(() => {
    const isGe = gePcsList.ge_list?.find((ge) => ge.label === profile?.user_name);
    if (isGe) {
      setDefaultInfo({
        ...formik.initialValues,
        record_creator: isGe.value as number,
      });
    }
  }, [gePcsList.ge_list]);

  const formik = useFormik({
    initialValues: defaultInfo,
    validationSchema: loseSchema,
    enableReinitialize: true, // initialValues默认值受控
    onSubmit(values, actions) {
      if (
        values.lose_status >= 1 &&
        values.lose_status <= 4 &&
        !values.member_id &&
        !values.not_member_id
      ) {
        return formik.setFieldError('content', '请匹配挂失人/领取人');
      }
      if (formik.isValid) {
        modal.show({
          contentText: '是否确认提交本次记录？',
          submitText: '保存',
          cancelText: '暂不',
          onSubmit: () => {
            const data = Object.assign(
              {},
              _.omit(values, [
                'content',
                !values.member_id && 'member_id',
                !values.not_member_id && 'not_member_id',
              ])
            );
            addEditLose(data).then((res) => {
              const { id } = res;
              if (id) {
                Alert.success(res?.msg ?? '保存成功');
                // 创建成功后跳到详情页
                formik.resetForm(); // 重置后不会被guard拦截
                push(`/site-service/record/info/l-and-f/${id}`);
              }
            });
          },
        });
      }
    },
  });

  const loading = useLoading(gePcsListLoading || loseBasicLoading);

  return (
    <FormikProvider value={formik}>
      {loading && <PageLoader />}
      <Form guard>
        <Panel title="新建失物招领" leftShadow loading={addEditLoseLoading}>
          <Card horizontal smDivider style={{ width: '75%' }}>
            <Card.Picker
              required
              label="服务状态"
              formKey="lose_status"
              pickerData={loseBasic.lose_status}
            />
            <Card.Picker
              required
              label="记录创建人"
              formKey="record_creator"
              pickerData={gePcsList.ge_list}
              searchable
            />
            <Card.Picker
              label="记录关闭人"
              formKey="record_close"
              pickerData={gePcsList.ge_list}
              searchable
            />
          </Card>
        </Panel>
        <div className="flex">
          <Panel title="挂失人/领取人信息" leftShadow className="flex-1">
            <Card horizontal smDivider>
              <Card.Input // 如果服务状态是失物(1-4)，挂失人/领取人字段必填
                {...(formik.values?.lose_status >= 1 &&
                  formik.values?.lose_status <= 4 && {
                    required: true,
                  })}
                className="flex items-center"
                label="挂失人/领取人"
                formKey="content"
                value={content}
                onChange={setContent}
              />
              <div className="flex-1 flex items-center">
                <div className="w-[30px] h-[47px] ml-[10px] flex items-center">
                  <IconButton
                    style={{ width: 30, backgroundColor: 'transparent' }}
                    onClick={() => {
                      fetchServiceMemberInfo({ content }).then((res) => {
                        if (res?.status === SERVICE_STATUS.SUCCESS) {
                          if (res?.data?.id) {
                            Alert.success('匹配成功');
                            const { id, level } = res.data;
                            formik.setValues({
                              ...formik.values,
                              ...(level === LEVEL_TYPE.NOT
                                ? { not_member_id: id }
                                : { member_id: id }),
                            });
                            setCurrentMember(res.data);
                          } else {
                            modal.show({
                              contentText: '匹配失败，是否创建非会员账户？',
                              submitText: '是',
                              cancelText: '取消',
                              onSubmit() {
                                push('/customer-account/no-member/create');
                              },
                            });
                          }
                        }
                      });
                    }}
                  >
                    <Image src="/resources/images/match.svg" />
                  </IconButton>
                </div>
                {currentMember?.id && (
                  <Card.Input
                    className="flex-1"
                    notEditable
                    label="客户名"
                    formKey="name"
                    value={currentMember?.name}
                    autoWidth
                    hasBtn
                    customBtn={
                      <IconButton
                        style={{ width: 20, backgroundColor: 'transparent' }}
                        onClick={() => {
                          window.open(
                            getPathname(
                              `/customer-account/${
                                currentMember?.level === LEVEL_TYPE.NOT
                                  ? ACCOUNT_TABS.NO_MEMBER
                                  : ACCOUNT_TABS.MEMBER
                              }/detail/${currentMember?.id}`
                            )
                          );
                        }}
                      >
                        <Image
                          src={`/resources/images/user${
                            currentMember?.sex === '男'
                              ? '-man'
                              : currentMember?.sex === '女'
                              ? '-woman'
                              : ''
                          }.svg`}
                        />
                      </IconButton>
                    }
                  />
                )}
              </div>
            </Card>
          </Panel>
          <Panel title="拾到人信息" leftShadow className="flex-1">
            <Card smDivider>
              <div className="flex items-center">
                <Card.Input // 如果服务状态是招领(5-9)，拾到人姓名字段必填
                  {...(formik.values?.lose_status >= 5 &&
                    formik.values?.lose_status <= 9 && {
                      required: true,
                    })}
                  label="拾到人姓名"
                  formKey="pick_name"
                />
                <Card.DatePicker
                  label="拾到时间"
                  formKey="pick_date_at"
                  format={BICESTER_FULL_DATE_HOURS_MINUTES}
                  placement="bottomEnd"
                />
              </div>
              <div className="w-[50%]">
                <Card.Picker
                  label="拾到人所在团队"
                  formKey="pick_team_id"
                  pickerData={loseBasic.pick_team}
                />
              </div>
              <Card.Input
                label="拾到备注"
                formKey="pick_remark"
                inputType="textarea"
                labelWeight={1}
                contentWeight={3}
              />
            </Card>
          </Panel>
        </div>
        <Panel
          title="物品信息"
          leftShadow
          toolbar={
            <div className="flex justify-between w-fit">
              <IconButton
                className="flex items-center"
                onClick={() => {
                  modal.show({
                    contentText: '确定要重置物品信息吗？',
                    submitText: '确定',
                    cancelText: '取消',
                    onSubmit: () => {
                      formik.setValues({
                        ...formik.values,
                        item_no: '',
                        item_type: '',
                        item_is_valuable: null,
                        item_pick_addr: '',
                        item_deposit_addr_id: null,
                        item_validity_date: '',
                        item_remark: '',
                      });
                    },
                  });
                }}
              >
                <Image src="/resources/images/reset.svg" />
                <div className="text-[12px] text-[#999999] ml-[5px]">重置</div>
              </IconButton>
            </div>
          }
        >
          <Card smDivider>
            <div className="flex items-center">
              <Card.Input label="物品编号" formKey="item_no" />
              <Card.Picker
                required
                label="物品类型"
                formKey="item_type"
                pickerData={loseBasic.item_type}
                searchable
                placement="topStart"
              />
              <Card.Picker label="是否为高额物品" formKey="item_is_valuable" pickerType="TorF" />
              <Card.Input label="物品找到/失踪地点" formKey="item_pick_addr" />
            </div>
            <div className="flex items-center">
              <Card.Picker
                label="存放地点"
                formKey="item_deposit_addr_id"
                pickerData={loseBasic.deposit_addr}
                searchable
                placement="topStart"
              />
              <Card.DatePicker
                label="物品到期日"
                formKey="item_validity_date"
                disabledDate={(date) => isBefore(date, subDays(new Date(), 1))}
                placement="topStart"
              />
              <Card.Input
                className="flex-[2]"
                label="物品描述"
                formKey="item_remark"
                labelWeight={1}
                contentWeight={3}
              />
            </div>
          </Card>
          <div className="flex justify-end my-[10px]">
            <Button type="submit" className="px-[70px]">
              确认提交
            </Button>
          </div>
        </Panel>
      </Form>
    </FormikProvider>
  );
};

export default CreateLose;
